/**
 * Static images setup
 */

import { assetUrl, getSceneImagePath, applyResponsiveImage } from '../../../utils/pathResolver'
import { evaluateCondition } from '../utils/condition-evaluator'
import type { AnimationContext } from './types'

/**
 * Setup static images for a scene
 */
export function setupStaticImages(context: AnimationContext): void {
  if (!context.data.images) return

  const target = context.data.imagesTarget === 'scene' ? context.container : context.imageContainer

  if (!target) return

  const revealAt = context.data.imagesReveal

  const setup = () => {
    const img = context.imageContainer?.querySelector('img')
    if (!img || img.complete === false) {
      setTimeout(setup, 100)
      return
    }

    requestAnimationFrame(() => {
      const filtered = context.data.images!.filter((cfg) => !cfg.when || evaluateCondition(cfg.when))

      let observer: IntersectionObserver | null = null
      if (revealAt !== undefined) {
        const bottomMargin = 100 - revealAt
        observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                const el = entry.target as HTMLElement
                el.style.opacity = '1'
                observer!.unobserve(el)
              }
            }
          },
          { rootMargin: `0px 0px -${bottomMargin}% 0px` },
        )
      }

      filtered.forEach((imageConfig) => {
        const staticImg = document.createElement('img')
        staticImg.src = imageConfig.image.startsWith('/')
          ? assetUrl(imageConfig.image)
          : getSceneImagePath(context.data.id, imageConfig.image)
        staticImg.className = 'static-image'
        staticImg.style.position = 'absolute'
        staticImg.style.height = 'auto'
        staticImg.style.width = `${imageConfig.width}%`
        staticImg.style.top = `${imageConfig.position.y}%`
        staticImg.style.left = `${imageConfig.position.x}%`
        const rot = imageConfig.rotation
        if (rot) staticImg.style.transform = `rotate(${rot}deg)`
        if (imageConfig.zIndex !== undefined) {
          staticImg.style.zIndex = imageConfig.zIndex.toString()
        }
        if (observer) {
          staticImg.style.opacity = '0'
          staticImg.style.transition = 'opacity 0.5s ease-out'
        }
        staticImg.alt = ''
        applyResponsiveImage(staticImg)
        target.appendChild(staticImg)
        if (observer) observer.observe(staticImg)
      })
    })
  }

  requestAnimationFrame(() => {
    setTimeout(setup, 200)
  })
}
